import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaPlus, FaTrash, FaArrowLeft } from 'react-icons/fa';
import { FormQuestionType } from '../../../../models/forms.model';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import { formsService } from '../../../../services/formsService';
import Button from '../../../../components/misc/Button';
import ParticipantPicker from '../../../../components/misc/ParticipantPicker';
import GenericInput from '../../../../components/forms/GenericInput';
import DateTimePicker from '../../../../components/forms/DateTimePicker';
import DropdownSelect from '../../../../components/misc/DropdownSelect';

type QuestionDraft = {
  localId: string;
  type: FormQuestionType;
  label: string;
  mandatory: boolean;
  options?: { label: string }[];
  meta?: any;
};

function SortableQuestion({
  q,
  idx,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  total,
  updateQuestion,
  removeQuestion,
  t,
}: {
  q: QuestionDraft;
  idx: number;
  total: number;
  updateQuestion: (index: number, updater: Partial<QuestionDraft>) => void;
  removeQuestion: (index: number) => void;
  t: (key: string) => string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.localId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      role="listitem"
      style={style}
      className="w-full rounded-lg bg-secondary-50 p-4 shadow-sm transition hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            {...attributes}
            {...listeners}
            className="text-secondary-600 hover:bg-secondary-100 mr-3 cursor-grab select-none rounded p-2"
            title={t('general.drag')}>
            ⋮
          </div>
          <div className="flex items-center gap-3">
            <div className="font-medium text-primary-900">{`${idx + 1}.`}</div>
            <div className="font-medium text-primary-900">{t(`component.eventPollForm.types.${q.type}`)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="tertiary"
            className="p-2"
            onClick={() => removeQuestion(idx)}
            title={t('general.delete')}
            aria-label={t('general.delete')}>
            <FaTrash className="h-4 w-4 text-error" />
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <div className="w-full">
          <GenericInput
            label=""
            type="text"
            value={q.label}
            onChange={(e) => updateQuestion(idx, { label: e.target.value })}
            placeholder={t('component.formBuilder.questionLabel')}
          />
        </div>

        <div className="flex items-center gap-3">
          <label className="text-secondary-600 text-sm">{t('general.mandatory')}?</label>
          <input
            type="checkbox"
            checked={q.mandatory}
            onChange={(e) => updateQuestion(idx, { mandatory: e.target.checked })}
          />
        </div>

        {q.type === FormQuestionType.CHOICE && (
          <div className="flex flex-col gap-2 rounded p-3">
            {(q.options || []).map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <GenericInput
                  label=""
                  type="text"
                  value={opt.label}
                  onChange={(e) =>
                    updateQuestion(idx, {
                      options: q.options!.map((o, j) => (j === oi ? { label: e.target.value } : o)),
                    })
                  }
                  placeholder={t('component.eventPollForm.optionPlaceholder') || 'Option'}
                />
                <Button
                  variant="tertiary"
                  className="px-2 py-1 text-sm"
                  onClick={() => updateQuestion(idx, { options: q.options!.filter((_, j) => j !== oi) })}>
                  {t('general.delete')}
                </Button>
              </div>
            ))}
            <Button
              className="p-0 text-sm underline"
              onClick={() => updateQuestion(idx, { options: [...(q.options || []), { label: '' }] })}>
              {t('component.eventPollForm.addOption')}
            </Button>
            {q.type === FormQuestionType.CHOICE && (
              <div className="mt-2 w-full max-w-xs">
                <GenericInput
                  label={t('component.formBuilder.choice.maxAnswers')}
                  type="number"
                  value={String(q.meta?.maxAnswers ?? 1)}
                  onChange={(e) =>
                    updateQuestion(idx, {
                      meta: { ...(q.meta || {}), maxAnswers: e.target.value ? Number(e.target.value) : undefined },
                    })
                  }
                  placeholder="1"
                />
              </div>
            )}
          </div>
        )}

        {q.type === FormQuestionType.NUMBER && (
          <div className="mt-2 flex gap-2">
            <GenericInput
              label={t('component.formBuilder.number.min')}
              type="number"
              value={q.meta?.min ?? ''}
              onChange={(e) =>
                updateQuestion(idx, {
                  meta: { ...(q.meta || {}), min: e.target.value ? Number(e.target.value) : undefined },
                })
              }
              placeholder="Min"
            />
            <GenericInput
              label={t('component.formBuilder.number.max')}
              type="number"
              value={q.meta?.max ?? ''}
              onChange={(e) =>
                updateQuestion(idx, {
                  meta: { ...(q.meta || {}), max: e.target.value ? Number(e.target.value) : undefined },
                })
              }
              placeholder="Max"
            />
          </div>
        )}

        {q.type === FormQuestionType.RATING && (
          <div className="mt-2 flex gap-2">
            <div className="w-full max-w-xs">
              <DropdownSelect<number>
                label={t('component.formBuilder.rating.scale')}
                options={[
                  { label: '1-3', value: 3 },
                  { label: '1-5', value: 5 },
                  { label: '1-10', value: 10 },
                ]}
                value={q.meta?.scale ?? 5}
                setValue={(v) => updateQuestion(idx, { meta: { ...(q.meta || {}), scale: v ?? 5 } })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CreateFormPage(): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState<Date | null | undefined>(null);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [responsesCount, setResponsesCount] = useState<number>(0);
  const [originalQuestionsNormalized, setOriginalQuestionsNormalized] = useState<any[] | null>(null);

  const [resultsVisibility, setResultsVisibility] = useState<'PUBLIC' | 'PRIVATE'>('PUBLIC');
  const [participantMode, setParticipantMode] = useState<'EVERYONE' | 'SELECTED'>('EVERYONE');
  const [selectedParticipants, setSelectedParticipants] = useState<number[]>([]);

  const { formId } = useParams();
  const isEdit = !!formId;

  useEffect(() => {
    if (!isEdit || !community?.id) return;
    (async () => {
      try {
        const f = await formsService.getFormBySlug(community.id, formId!);
        setTitle(f.title ? String(f.title).trim() : '');
        setDescription(f.description || '');
        setDeadline(f.deadline ? new Date(f.deadline) : null);
        setResultsVisibility(f.resultsVisibility ?? 'PUBLIC');

        const loadedParticipants = (f.participants ?? []) as any[];
        const participantMemberIds = loadedParticipants
          .filter((p) => p.dashboardMemberId)
          .map((p) => p.dashboardMemberId);
        setSelectedParticipants(participantMemberIds);
        setParticipantMode(participantMemberIds.length > 0 ? 'SELECTED' : 'EVERYONE');
        const normalizedLoadedQuestions = (f.questions || []).map((q) => ({
          localId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          type: q.type,
          label: q.label ? String(q.label).trim() : '',
          mandatory: !!q.mandatory,
          options: q.options ? q.options.map((o) => ({ label: o.label ? String(o.label).trim() : '' })) : undefined,
          meta: q.meta || undefined,
        }));
        setQuestions(normalizedLoadedQuestions);
        setResponsesCount((f.responses || []).length);
        setOriginalQuestionsNormalized(
          normalizedLoadedQuestions.map((q) => ({
            type: q.type,
            label: q.label,
            mandatory: q.mandatory,
            meta: q.meta ?? null,
            options: q.options ? q.options.map((o) => String(o.label).trim()).filter((l) => l !== '') : [],
          })),
        );
      } catch (e) {
        console.error('Failed to load form:', e);
      }
    })();
  }, [formId, community?.id, isEdit]);

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  if (community.role !== 'ADMIN') {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('error.unauthorized')}</p>
      </div>
    );
  }

  const addQuestion = (type: FormQuestionType) => {
    const q: QuestionDraft = {
      localId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      label: '',
      mandatory: false,
      options: type === FormQuestionType.CHOICE ? [{ label: '' }, { label: '' }] : undefined,
      meta:
        type === FormQuestionType.NUMBER
          ? { min: undefined, max: undefined }
          : type === FormQuestionType.RATING
            ? { scale: 5 }
            : type === FormQuestionType.CHOICE
              ? { maxAnswers: 1 }
              : undefined,
    };
    setQuestions((s) => [...s, q]);
  };

  const updateQuestion = (index: number, updater: Partial<QuestionDraft>) => {
    setQuestions((s) => s.map((q, i) => (i === index ? { ...q, ...updater } : q)));
  };

  const removeQuestion = (index: number) => setQuestions((s) => s.filter((_, i) => i !== index));

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over) return;
    if (active.id !== over.id) {
      setQuestions((s) => {
        const oldIndex = s.findIndex((x) => x.localId === active.id);
        const newIndex = s.findIndex((x) => x.localId === over.id);
        return arrayMove(s, oldIndex, newIndex);
      });
    }
  };

  const validate = (): string | null => {
    const errors: string[] = [];

    if (!String(title || '').trim()) errors.push(t('component.formBuilder.errors.emptyTitle'));
    if (questions.length === 0) errors.push(t('component.formBuilder.errors.noQuestions'));

    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i];
      if (!String(q.label || '').trim()) errors.push(`${t('component.formBuilder.errors.emptyQuestion')} (${i + 1})`);
      if (q.type === FormQuestionType.CHOICE) {
        if (!q.options || q.options.length < 2)
          errors.push(`${t('component.formBuilder.errors.emptyOption')} (${i + 1})`);
        if (q.options && q.options.some((o) => !String(o.label || '').trim()))
          errors.push(`${t('component.formBuilder.errors.emptyOption')} (${i + 1})`);
        const max = q.meta?.maxAnswers !== undefined && q.meta?.maxAnswers !== null ? Number(q.meta.maxAnswers) : 1;
        if (Number.isNaN(max) || max < 1 || (q.options && max > q.options.length))
          errors.push(`${t('component.formBuilder.errors.invalidMaxAnswers')} (${i + 1})`);
      }
      if (q.type === FormQuestionType.NUMBER) {
        const min = q.meta?.min;
        const max = q.meta?.max;
        if (min !== undefined && max !== undefined && Number(min) > Number(max))
          errors.push(`${t('component.formBuilder.errors.invalidNumberRange')} (${i + 1})`);
      }
      if (q.type === FormQuestionType.RATING) {
        const raw = q.meta && q.meta.scale !== undefined && q.meta.scale !== null ? q.meta.scale : undefined;
        const scale = raw !== undefined && raw !== '' ? Number(raw) : 5;
        if (Number.isNaN(scale) || scale < 2)
          errors.push(`${t('component.formBuilder.errors.invalidRatingScale')} (${i + 1})`);
      }
    }

    return errors.length ? errors.join('. ') : null;
  };

  const validationError = useMemo(() => validate(), [title, questions, t]);
  const canSave = useMemo(() => !validationError, [validationError]);

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    if (!community?.id) {
      toast.error(t('error.noCommunitySelected'));
      return;
    }

    try {
      type CreateFormPayload = {
        title: string;
        description?: string;
        deadline?: string | null;
        questions: Array<{
          type: FormQuestionType;
          label: string;
          mandatory: boolean;
          options?: { label: string }[];
          meta?: any;
        }>;
        resultsVisibility?: 'PUBLIC' | 'PRIVATE';
        participants?: any[];
      };

      const body: CreateFormPayload = {
        title: title.trim(),
        description: description.trim() || undefined,
        deadline: deadline ? new Date(deadline).toISOString() : null,
        questions: questions.map((q) => ({
          type: q.type,
          label: q.label.trim(),
          mandatory: q.mandatory,
          options: q.options ? q.options.map((o) => ({ label: o.label.trim() })) : undefined,
          meta:
            q.type === FormQuestionType.RATING
              ? { ...(q.meta || {}), scale: q.meta && q.meta.scale !== undefined ? q.meta.scale : 5 }
              : q.type === FormQuestionType.CHOICE
                ? {
                    ...(q.meta || {}),
                    maxAnswers: q.meta && q.meta.maxAnswers !== undefined ? Number(q.meta.maxAnswers) : 1,
                  }
                : q.meta
                  ? q.meta
                  : undefined,
        })),
        resultsVisibility,
        participants:
          participantMode === 'SELECTED'
            ? selectedParticipants.map((memberId) => ({ dashboardMemberId: memberId, type: 'COMMUNITY' }))
            : [],
      };

      if (isEdit) {
        // Only warn when questions changed and there are existing responses
        const normalize = (qs: any[]) =>
          qs.map((q: any) => ({
            type: q.type,
            label: q.label ? String(q.label).trim() : '',
            mandatory: !!q.mandatory,
            meta: q.meta ?? null,
            options: q.options ? q.options.map((o: any) => String(o.label).trim()).filter((l: string) => l !== '') : [],
          }));

        const currentNorm = normalize(questions);
        const originalNorm = originalQuestionsNormalized || [];
        const questionsChanged = JSON.stringify(currentNorm) !== JSON.stringify(originalNorm);

        if (responsesCount > 0 && questionsChanged) {
          // eslint-disable-next-line no-alert
          const confirmed = window.confirm(t('component.formBuilder.resetResponsesConfirm', { count: responsesCount }));
          if (!confirmed) return;
        }

        await formsService.updateForm(community.id, formId!, body);
        toast.success(t('toast.success.communitySaved'));
        if (questionsChanged) {
          setResponsesCount(0);
          setOriginalQuestionsNormalized(currentNorm);
        }
        navigate(`/community/forms/${formId}`);
      } else {
        const created = await formsService.createForm(community.id, body);
        toast.success(t('toast.success.communitySaved'));
        navigate(`/community/forms/${created.id}`);
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.error || e?.message || t('error.formSaveFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/community/forms')}
          className="hover:bg-secondary-100 rounded-full p-2 transition-colors">
          <FaArrowLeft className="h-5 w-5 text-primary" />
        </button>
        <h1 className="text-2xl font-bold text-primary">
          {isEdit ? t('page.dashboard.forms.edit') : t('page.dashboard.forms.create')}
        </h1>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 rounded-lg bg-secondary-50 p-6">
          <GenericInput
            label={t('component.formBuilder.titleLabel')}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('component.formBuilder.titlePlaceholder') || 'Form title'}
            required
          />

          <GenericInput
            label={t('component.formBuilder.descriptionLabel')}
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('component.formBuilder.descriptionPlaceholder') || 'Form description'}
            textarea
          />

          <DateTimePicker
            label={t('component.formResponse.deadlineLabel')}
            value={deadline}
            onChange={(d) => setDeadline(d)}
            timeEnabled
            className="!bg-secondary-50"
            placeholder={t('component.formBuilder.deadlinePlaceholder') || 'Select deadline'}
          />

          <DropdownSelect<'PUBLIC' | 'PRIVATE'>
            label={t('component.formBuilder.resultsVisibility')}
            options={[
              { label: t('component.formBuilder.resultsVisibilityOptions.public'), value: 'PUBLIC' },
              { label: t('component.formBuilder.resultsVisibilityOptions.private'), value: 'PRIVATE' },
            ]}
            value={resultsVisibility}
            setValue={(v) => setResultsVisibility((v ?? 'PUBLIC') as 'PUBLIC' | 'PRIVATE')}
          />

          <DropdownSelect<'EVERYONE' | 'SELECTED'>
            label={t('component.formBuilder.participantMode')}
            options={[
              { label: t('component.formBuilder.participantModeEveryone'), value: 'EVERYONE' },
              { label: t('component.formBuilder.participantModeSelected'), value: 'SELECTED' },
            ]}
            value={participantMode}
            setValue={(v) => {
              setParticipantMode((v ?? 'EVERYONE') as 'EVERYONE' | 'SELECTED');
              if (v === 'EVERYONE') setSelectedParticipants([]);
            }}
          />

          {participantMode === 'SELECTED' && (
            <div className="flex flex-col gap-2">
              <label className="font-medium text-primary-900">{t('component.formBuilder.participants')}</label>
              <ParticipantPicker
                communityId={community?.id}
                selected={selectedParticipants}
                setSelected={setSelectedParticipants}
                placeholder={t('component.formBuilder.participantsDescription')}
              />
            </div>
          )}
        </div>

        {questions.length !== 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={questions.map((q) => q.localId)} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-3">
                {questions.map((q, idx) => (
                  <SortableQuestion
                    key={q.localId}
                    q={q}
                    idx={idx}
                    total={questions.length}
                    updateQuestion={updateQuestion}
                    removeQuestion={removeQuestion}
                    t={t}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="rounded-lg border-2 border-dashed border-secondary-200 p-6 text-center">
          <div className="mb-3 font-medium text-primary-900">{t('component.formBuilder.addQuestions')}</div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="primary-outline"
              className="flex items-center gap-2 shadow-sm"
              onClick={() => addQuestion(FormQuestionType.CHOICE)}>
              <FaPlus className="h-4 w-4" />
              {t('component.eventPollForm.types.CHOICE')}
            </Button>
            <Button
              variant="primary-outline"
              className="flex items-center gap-2 shadow-sm"
              onClick={() => addQuestion(FormQuestionType.TEXT)}>
              <FaPlus className="h-4 w-4" />
              {t('component.eventPollForm.types.TEXT')}
            </Button>
            <Button
              variant="primary-outline"
              className="flex items-center gap-2 shadow-sm"
              onClick={() => addQuestion(FormQuestionType.NUMBER)}>
              <FaPlus className="h-4 w-4" />
              {t('component.eventPollForm.types.NUMBER')}
            </Button>
            <Button
              variant="primary-outline"
              className="flex items-center gap-2 shadow-sm"
              onClick={() => addQuestion(FormQuestionType.RATING)}>
              <FaPlus className="h-4 w-4" />
              {t('component.eventPollForm.types.RATING')}
            </Button>
            <Button
              variant="primary-outline"
              className="flex items-center gap-2 shadow-sm"
              onClick={() => addQuestion(FormQuestionType.DATE)}>
              <FaPlus className="h-4 w-4" />
              {t('component.eventPollForm.types.DATE')}
            </Button>
          </div>
        </div>

        <div className="flex flex-col justify-end gap-2">
          {validationError && (
            <div className="mb-2 text-left">
              <div
                className="inline-block w-full rounded bg-red-100 p-3 text-sm text-red-700"
                role="alert"
                aria-live="assertive">
                {t('component.formBuilder.errors.fixErrors')}: {validationError}
              </div>
            </div>
          )}

          <div className="ml-auto flex gap-2">
            <Button onClick={() => navigate('/community/forms')}>{t('general.cancel')}</Button>
            <Button variant="primary" onClick={submit} disabled={!canSave}>
              {t('general.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
